import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types';

const NEWS_MAX_HEIGHT = '380px';

function TwemojiText({ children }) {
  const emoji = typeof children === 'string' ? children.trim() : '';
  const codePoints = [...emoji]
    .map((c) => c.codePointAt(0).toString(16))
    .join('-');
  const src = `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codePoints}.svg`;

  return (
    <img
      className="twemoji"
      draggable={false}
      src={src}
      alt={emoji}
    />
  );
}

TwemojiText.propTypes = {
  children: PropTypes.string.isRequired,
};

function NewsRow({ month, year, isMobile, children }) {
  return (
    <Box sx={{ pl:0.5, display: 'flex', flexDirection: 'row', alignItems: 'center', pb: 1 }}>
      <Typography
        sx={{
          fontSize: '1rem',
          color: 'grey.500',
          fontWeight: 'bold',
          flexShrink: 0,
          width: isMobile? '60px': '120px'
        }}
      >
        <span style={{ display: 'inline-block', width: '36px' }}>{month}</span>{year}
      </Typography>
      <Typography sx={{ fontSize: '1rem' }}>
        {children}
      </Typography>
    </Box>
  );
}

NewsRow.propTypes = {
  month: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default function News({ isMobile }) {
  const scrollRef = useRef(null);
  const [atBottom, setAtBottom] = useState(false);

  useLayoutEffect(() => {
    // Always start with the latest news when this section is mounted.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setAtBottom(false);
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  };

  const newsItems = [
    { month: 'Oct.', year: '2026', content: (
      <>Honored to be invited to <a href="https://rsvp.withgoogle.com/events/bench-summit-by-kaggle" target="_blank" rel="noopener noreferrer">Bench Summit by Kaggle</a> and selected as a speaker to present <a href="https://microsoft.github.io/debug-gym/static/papers/ProgramDistill_arxiv.pdf" target="_blank" rel="noopener noreferrer">ProgramDistill</a>! See you in SF on October 28 <TwemojiText>🇺🇸</TwemojiText></>
    ) },
    { month: 'Oct.', year: '2026', content: (
      <>Two papers have been accepted to the NeurIPS26 workshops on Interpreting Agent Behavior and Transitioning from Pre-Training to Post-Training!</>
    ) },
    { month: 'Sep.', year: '2026', content: (
      <>Gave an invited talk on <a href="https://microsoft.github.io/debug-gym/static/papers/ProgramDistill_arxiv.pdf" target="_blank" rel="noopener noreferrer">ProgramDistill</a> at <a href="https://coco-kaist.github.io/" target="_blank" rel="noopener noreferrer">KAIST COCO Lab</a>.</>
    ) },
    { month: 'Sep.', year: '2026', content: (
      <>We released <a href="https://huggingface.co/microsoft/FrogNano-4B-2609" target="_blank" rel="noreferrer">FrogNano-4B</a>, our 4B coding agent trained via online task synthesis! Model weights are now available on Hugging Face.</>
    ) },
    { month: 'Sep.', year: '2026', content: (
      <>We released <strong>ProgramDistill-Bench</strong>, a new SWE benchmark with <strong>4,096 tasks</strong> where agents interact with a reference app and reimplement its functionality.
        <br />
        <TwemojiText>🏆</TwemojiText> <a href="https://microsoft.github.io/debug-gym/blog/2026/09/programdistill/dashboard/" target="_blank" rel="noreferrer">Leaderboard</a>
        {' · '}
        <TwemojiText>🚀</TwemojiText> <a href="https://huggingface.co/datasets/microsoft/ProgramDistill" target="_blank" rel="noreferrer">Try ProgramDistill-Bench</a>
      </>
    ) },
    { month: 'Sep.', year: '2026', content: (
      <>Our <a href="https://arxiv.org/abs/2605.10781" target="_blank" rel="noreferrer">Rebellious Student</a> has been accepted to NeurIPS26! See you in Sydney <TwemojiText>🇦🇺</TwemojiText></>
    ) },
    { month: 'Aug.', year: '2026', content: (
      <>Our <a href="https://arxiv.org/abs/2606.29985" target="_blank" rel="noreferrer">LLM reasoning diversity analysis paper</a> has been accepted to EMNLP26 Main!</>
    ) },
    { month: 'Aug.', year: '2026', content: (
      <>Three papers have been accepted to COLM26 workshops, with <strong>one oral presentation, one spotlight, and one travel grant</strong>!</>
    ) },
    { month: 'Jul.', year: '2026', content: (
      <>Our <a href="https://arxiv.org/abs/2603.24472" target="_blank" rel="noreferrer">self-distillation analysis paper</a> has been accepted to COLM26! See you in SF this October <TwemojiText>🇺🇸</TwemojiText></>
    ) },
    { month: 'Jun.', year: '2026', content: (
      <>Gave a talk at <a href="https://www.upstage.ai/" target="_blank" rel="noopener noreferrer">Upstage AI</a> on our MSRA work on self-distillation and exploration in LLM post-training — successes in long-horizon agent setups and failure modes (and directions) in math reasoning. <a href={`${process.env.PUBLIC_URL}/slides/upstage_0626.pdf`} target="_blank" rel="noopener noreferrer">[slides]</a></>
    ) },
    { month: 'Jun.', year: '2026', content: (
      <>Two papers, including one spotlight, have been accepted to the ICML26 AI for Math Workshop!</>
    ) },
    { month: 'May.', year: '2026', content: (
      <>Selected as a <b>Silver Reviewer</b> for the ICML26.</>
    ) },
    { month: 'May.', year: '2026', content: (
      <><a href="https://arxiv.org/abs/2602.12642" target="_blank" rel="noopener noreferrer">PACED-RL</a> is accepted to ICML26<TwemojiText> 🇰🇷</TwemojiText></>
    ) },
    { month: 'Mar.', year: '2026', content: (
      <>Awarded the <b>Outstanding Research Achievement Award</b> in KAIST Electrical Engineering.</>
    ) },
    { month: 'Mar.', year: '2026', content: (
      <>Honored to receive the <b>"Stars of Tomorrow" (Excellent Intern Award)</b> during my internship at MSRA!</>
    ) },
    { month: 'Feb.', year: '2026', content: (
      <><a href="https://arxiv.org/abs/2512.24461" target='_blank' rel="noreferrer">AWS</a> has been accepted to CVPR26!</>
    ) },
    { month: 'Jan.', year: '2026', content: (
      <><a href="https://beanie00.github.io/empo2/" target='_blank' rel="noreferrer">EMPO<sup>2</sup></a>, conducted during my MSRA internship, has been accepted to ICLR26 (scores: 10,8,6,6)!</>
    ) },
    { month: 'Dec.', year: '2025', content: (
      <>
        I'll be joining the{' '}
        <a href="https://microsoft.github.io/debug-gym/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Microsoft Research Froggy team
        </a>
        {' '}at Montréal in summer 2026 <TwemojiText>🇨🇦</TwemojiText>!
      </>
    ) },
    { month: 'Aug.', year: '2025', content: (
      <>
        I've started my internship at <a href="https://github.com/microsoft/agent-lightning/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Microsoft Research Agent Lightning team
        </a>  at Shanghai! Also, <a href="https://arxiv.org/abs/2505.15182" target='_blank' rel="noreferrer">ReflAct</a> has been accepted to EMNLP25 and will be presented in Suzhou, China <TwemojiText>🇨🇳</TwemojiText>
      </>
    ) },
    { month: 'May.', year: '2025', content: (
      <>
        Three papers, including one spotlight poster, have been accepted to ICML25! You can check the related news articles in Korean at <a
          href="https://n.news.naver.com/mnews/article/001/0015450553?sid=105"
          target="_blank"
          rel="noopener noreferrer"
        >
          [Link1]
        </a> and <a
          href="https://ee.kaist.ac.kr/research-achieve/%ec%84%b1%ec%98%81%ec%b2%a0-%ea%b5%90%ec%88%98-%ec%97%b0%ea%b5%ac%ec%8b%a4-%ea%b9%80%ec%a0%95%ed%98%9c-%eb%b0%95%ec%82%ac%ea%b3%bc%ec%a0%95%ec%83%9d-ai%eb%a1%9c-%ec%82%b0%ec%97%85-%ed%98%84%ec%9e%a5/"
          target="_blank"
          rel="noopener noreferrer"
        >
          [Link2]
        </a>.
      </>
    ) },
    { month: 'Sep.', year: '2024', content: (
      <><a href="https://arxiv.org/abs/2402.02017" target='_blank' rel="noreferrer">QCS</a> has been accepted to NeurIPS24 and will be presented in Vancouver, Canada <TwemojiText>🇨🇦</TwemojiText></>
    ) },
    { month: 'Jan.', year: '2024', content: (
      <><a href="https://openreview.net/forum?id=af2c8EaKl8" target='_blank' rel="noreferrer">DC</a> has been accepted to ICLR24 as a spotlight presentation! See you in Vienna, Austria <TwemojiText>🇦🇹</TwemojiText></>
    ) },
    { month: 'Mar.', year: '2024', content: (
      <>I've started as a research intern at <a href="https://www.lgresearch.ai/" target='_blank' rel="noreferrer">LG AI Research <TwemojiText>🇰🇷</TwemojiText></a>!</>
    ) },
    { month: 'Apr.', year: '2023', content: (
      <>My first paper, <a href="https://openreview.net/forum?id=vXcvrYJlVm" target='_blank' rel="noreferrer"> LESSON</a>, has been accepted to ICML23! I hope to see you in Hawaii, USA <TwemojiText>🇺🇸</TwemojiText></>
    ) },
  ];

  return (
    <>
      <style>{`
        img.twemoji {
          height: 1em;
          width: 1em;
          margin: 0 0.05em 0 0.1em;
          vertical-align: -0.1em;
        }
      `}</style>
      <Box sx={{ position: 'relative' }}>
        <Box
          ref={scrollRef}
          role="region"
          aria-label="News updates"
          onScroll={handleScroll}
          sx={{
            maxHeight: NEWS_MAX_HEIGHT,
            overflowY: 'scroll',
            overflowAnchor: 'none',
            pr: 1,
            // Force an always-visible scrollbar in Chrome/Safari (overrides macOS overlay).
            // Note: do NOT set `scrollbar-width` here — in modern Chrome it disables these.
            '&::-webkit-scrollbar': { width: '5px', WebkitAppearance: 'none' },
            '&::-webkit-scrollbar-track': { backgroundColor: '#F2F6FE', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#D3E1FD', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#b9d2fc' },
          }}
        >
          {newsItems.map((item, index) => (
            <NewsRow key={index} month={item.month} year={item.year} isMobile={isMobile}>
              {item.content}
            </NewsRow>
          ))}
        </Box>
        {/* Bottom fade hints there is more to scroll on every screen size. */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '34px',
            pointerEvents: 'none',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.95))',
            opacity: atBottom ? 0 : 1,
            transition: 'opacity 0.2s ease',
          }}
        />
      </Box>
    </>
  )
}

News.propTypes = {
  isMobile: PropTypes.bool.isRequired
};
